import { apiClient } from './apiClient';
import { useAuthStore } from '../stores/authStore';
import { isWeComClient } from '../utils/device';
import { shouldSuppressDuplicateWeComPreview } from './wecomPreviewGuard';
import * as ww from '@wecom/jssdk';

let observerStarted = false;
let isRouteListenerInitialized = false;
let lastInitializedUrl = '';
let cachedExtraJsApiList: string[] = [];
let openDataFrameReadyPromise: Promise<boolean> | null = null;
const scriptLoadPromises = new Map<string, Promise<void>>();
const reportedFrameErrors = new Set<string>();
let reportedFrameErrorCount = 0;
const WECOM_SCRIPT_TIMEOUT_MS = 10_000;
const WECOM_SDK_TIMEOUT_MS = 15_000;
const WECOM_FRAME_ERROR_REPORT_LIMIT = 8;
const WECOM_FRAME_JS_API_LIST = [
  'wwapp.invokeJsApiByCallInfo',
  'openEnterpriseChat',
  'getContext',
  'getCurExternalContact',
  'getCurExternalChat',
];
const WECOM_FRAME_INIT_RETRY_DELAYS_MS = [0, 400, 1000] as const;

type WeComSignatureConfig = {
  corp_id: string;
  agent_id: string | number;
  nonce_str: string;
  timestamp: string | number;
  signature: string;
  agent_signature: string;
};

const WECOM_ERROR_MESSAGE_KEYS = [
  'errMsg',
  'err_msg',
  'errmsg',
  'message',
  'detail',
  'error',
  'errorMessage',
] as const;

/**
 * The new WeCom SDK rejects JSAPI calls with a CommonResult object instead of
 * an Error instance. Some SDK versions also wrap that object in `message`.
 * Normalize both shapes so callers never surface "[object Object]" to users.
 */
export function getWeComSDKErrorMessage(error: unknown, fallback = ''): string {
  const visited = new Set<object>();

  const extract = (value: unknown): string => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (typeof value !== 'object') return String(value);
    if (visited.has(value)) return '';
    visited.add(value);

    const record = value as Record<string, unknown>;
    for (const key of WECOM_ERROR_MESSAGE_KEYS) {
      const message = extract(record[key]);
      if (message) return message;
    }
    return '';
  };

  return extract(error) || fallback;
}

const sanitizeTelemetry = (message: string) => message
  .replace(/([?&](?:access_token|code|ticket|secret|key)=)[^&\s]+/gi, '$1***')
  .replace(/(bearer\s+)[^\s]+/gi, '$1***')
  .slice(0, 500);

export function reportWeComFrameError(stage: string, error: unknown) {
  const detail = sanitizeTelemetry(getWeComSDKErrorMessage(error, String(error || '未知错误')));
  const log = `[OpenDataFrame:${stage}] ${detail}`;
  console.warn(log, error);

  if (reportedFrameErrorCount >= WECOM_FRAME_ERROR_REPORT_LIMIT || reportedFrameErrors.has(log)) {
    return;
  }
  reportedFrameErrors.add(log);
  reportedFrameErrorCount += 1;
  apiClient.get('/auth/wecom/js-config-telemetry', {
    params: { log },
    suppressErrorToast: true,
    timeout: 5000,
  } as any).catch(() => {});
}

export function sendTelemetry(msg: string) {
  // 正常运行保持静默，只上报失败阶段，避免会话页产生高频遥测请求。
  if (/(?:failed|error|aborted|timeout|失败|异常|超时)/i.test(msg)) {
    reportWeComFrameError('sdk', msg);
  }
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number, message: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error(message)), timeoutMs);
    promise.then(resolve, reject).finally(() => window.clearTimeout(timer));
  });
}

function loadScript(src: string): Promise<void> {
  const pending = scriptLoadPromises.get(src);
  if (pending) return pending;

  const promise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      if ((existing as HTMLScriptElement).dataset.hhcdLoaded === 'true') {
        resolve();
        return;
      }
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', reject, { once: true });
      return;
    }

    const script = document.createElement('script');
    const timer = window.setTimeout(() => {
      script.remove();
      reject(new Error(`企业微信脚本加载超时: ${src}`));
    }, WECOM_SCRIPT_TIMEOUT_MS);

    const cleanup = () => {
      script.removeEventListener('load', handleLoad);
      script.removeEventListener('error', handleError);
    };
    const handleLoad = () => {
      window.clearTimeout(timer);
      cleanup();
      script.dataset.hhcdLoaded = 'true';
      resolve();
    };
    const handleError = (err: Event) => {
      window.clearTimeout(timer);
      cleanup();
      reject(err);
    };

    script.addEventListener('load', handleLoad, { once: true });
    script.addEventListener('error', handleError, { once: true });
    script.async = true;
    script.src = src;
    script.referrerPolicy = 'origin';
    document.head.appendChild(script);
  });
  scriptLoadPromises.set(src, promise);
  promise.catch(() => {
    if (scriptLoadPromises.get(src) === promise) {
      scriptLoadPromises.delete(src);
    }
  });
  return promise;
}

async function loadWeComScripts(): Promise<boolean> {
  if ((window as any).wx && (window as any).WWOpenData) {
    return true;
  }
  try {
    sendTelemetry('Loading WeCom JSSDK scripts dynamically...');
    await loadScript('https://res.wx.qq.com/open/js/jweixin-1.2.0.js');
    await loadScript('https://open.work.weixin.qq.com/wwopen/js/jwxwork-1.0.0.js');
    sendTelemetry(`Scripts loaded dynamically. hasWx=${!!(window as any).wx}`);
    return true;
  } catch (err) {
    sendTelemetry(`Error: failed to load scripts dynamically: ${String(err)}`);
    return false;
  }
}

async function fetchWeComSignatureConfig(url: string): Promise<WeComSignatureConfig> {
  const res = await apiClient.get('/auth/wecom/js-config', {
    params: { url },
    suppressErrorToast: true,
  } as any);
  const configData = res.data?.data || res.data;
  if (!configData) {
    throw new Error('获取企业微信 JS-SDK 签名失败');
  }
  return configData as WeComSignatureConfig;
}

/**
 * 初始化新版 @wecom/jssdk 的 OpenDataFrame 能力，用于会话展示组件。
 * 会话展示组件必须使用 ww.register + ww.initOpenData，并在 jsApiList 中包含
 * wwapp.invokeJsApiByCallInfo。
 */
export async function initWeComOpenDataFrame(): Promise<boolean> {
  const currentUrl = window.location.href.split('#')[0];

  // ww.register 注册的是当前应用身份，签名回调会收到实际待签名 URL。
  // 因此同一页面生命周期内必须全局复用同一轮 register/initOpenData，
  // 不能在 SPA 从 / 跳转到 /conversations 时并发注册两次。
  if (openDataFrameReadyPromise) {
    return openDataFrameReadyPromise;
  }

  const initPromise = (async () => {
    sendTelemetry('initWeComOpenDataFrame started');
    const scriptsLoaded = await loadWeComScripts();
    if (!scriptsLoaded) {
      sendTelemetry('initWeComOpenDataFrame aborted: scripts not loaded');
      throw new Error('企业微信脚本未加载完成');
    }

    const initialConfig = await fetchWeComSignatureConfig(currentUrl);

    ww.register({
      corpId: initialConfig.corp_id,
      agentId: initialConfig.agent_id,
      // 会话页既要渲染安全展示组件，也要支持直接打开企微会话。
      jsApiList: WECOM_FRAME_JS_API_LIST,
      async getConfigSignature(url: string) {
        const config = await fetchWeComSignatureConfig(url || currentUrl);
        return {
          timestamp: config.timestamp,
          nonceStr: config.nonce_str,
          signature: config.signature,
        };
      },
      async getAgentConfigSignature(url: string) {
        const config = await fetchWeComSignatureConfig(url || currentUrl);
        return {
          timestamp: config.timestamp,
          nonceStr: config.nonce_str,
          signature: config.agent_signature,
        };
      },
      onConfigFail(error: unknown) {
        reportWeComFrameError('config', error);
      },
      onAgentConfigFail(error: unknown) {
        reportWeComFrameError('agent-config', error);
      },
    });

    const result = await withTimeout(
      ww.initOpenData(),
      WECOM_SDK_TIMEOUT_MS,
      '企业微信会话展示组件初始化超时',
    );
    sendTelemetry(`ww.initOpenData SUCCESS: ${JSON.stringify(result || {})}`);
    (window as any).__wecom_open_data_ready = true;
    (window as any).__wecom_ready = true;
    window.dispatchEvent(new CustomEvent('wecom-ready'));
    triggerWWOpenDataBind();
    startWWOpenDataObserver();
    return true;
  })().catch((err) => {
    reportWeComFrameError('init', err);
    if (openDataFrameReadyPromise === initPromise) {
      openDataFrameReadyPromise = null;
    }
    return false;
  });

  openDataFrameReadyPromise = initPromise;
  return initPromise;
}

function createWeComOpenDataFrameFactory() {
  return ww.createOpenDataFrameFactory();
}

export type WeComOpenDataFrameInstance = {
  setData?: (data: unknown) => void;
  dispose?: () => void;
  destroy?: () => void;
};

type MountWeComOpenDataFrameOptions = {
  isDisposed?: () => boolean;
  stage?: string;
};

/**
 * OpenDataFrame 的统一挂载入口。
 *
 * 页面只负责模板、数据和交互；SDK 就绪、失败重试、卸载竞态与创建异常由这里
 * 统一处理，避免不同菜单各自实现一套初始化时序。
 */
export async function mountWeComOpenDataFrame(
  config: Record<string, unknown>,
  options: MountWeComOpenDataFrameOptions = {},
): Promise<WeComOpenDataFrameInstance | null> {
  const isDisposed = options.isDisposed || (() => false);

  for (let attempt = 0; attempt < WECOM_FRAME_INIT_RETRY_DELAYS_MS.length; attempt += 1) {
    if (isDisposed()) return null;

    const delayMs = WECOM_FRAME_INIT_RETRY_DELAYS_MS[attempt];
    if (delayMs > 0) {
      await new Promise<void>((resolve) => window.setTimeout(resolve, delayMs));
      if (isDisposed()) return null;
    }

    if (await initWeComOpenDataFrame()) {
      if (isDisposed()) return null;
      try {
        const instance = createWeComOpenDataFrameFactory().createOpenDataFrame(
          config as any,
        ) as WeComOpenDataFrameInstance;
        if (isDisposed()) {
          disposeWeComOpenDataFrame(instance);
          return null;
        }
        return instance;
      } catch (error) {
        reportWeComFrameError(options.stage || 'mount', error);
        return null;
      }
    }
  }

  return null;
}

export function disposeWeComOpenDataFrame(
  instance: WeComOpenDataFrameInstance | null | undefined,
) {
  if (!instance) return;
  try {
    instance.dispose?.();
    instance.destroy?.();
  } catch (error) {
    reportWeComFrameError('dispose', error);
  }
}

export function createWeComScrollViewContext(instance: any, refName: string) {
  return ww.createScrollViewContext(instance, refName);
}

type WeComPreviewModalEvent = {
  modalUrl?: string;
  modalSize?: {
    width?: number;
    height?: number;
  };
};

export function openWeComPreviewModal({ modalUrl, modalSize }: WeComPreviewModalEvent): boolean {
  if (!modalUrl) return false;

  // 双击会让会话展示组件连续触发两次 handleModal。第二次返回“已处理”，
  // 由各调用方转换为 false，按官方文档阻止组件再次打开预览窗口；
  // 防重不依赖文档未承诺保持稳定的 modalUrl。
  if (shouldSuppressDuplicateWeComPreview()) return true;

  // 企业微信内置浏览器应交由会话展示组件执行原生预览，从聊天侧边栏外
  // 打开独立的大图/视频窗口。自定义 iframe 仅用于外部浏览器兜底。
  if (isWeComClient()) return false;

  const existing = document.getElementById('hhcd-wecom-preview-modal');
  if (existing) existing.remove();

  const viewportWidth = Math.max(window.innerWidth, 320);
  const viewportHeight = Math.max(window.innerHeight, 320);
  const maxWidth = Math.max(280, viewportWidth - 48);
  const maxHeight = Math.max(280, viewportHeight - 48);
  const preferredWidth = modalSize?.width ? Math.max(modalSize.width, Math.min(960, maxWidth)) : Math.min(960, maxWidth);
  const preferredHeight = modalSize?.height ? Math.max(modalSize.height, Math.min(720, maxHeight)) : Math.min(720, maxHeight);
  const modalWidth = Math.min(preferredWidth, maxWidth);
  const modalHeight = Math.min(preferredHeight, maxHeight);

  const overlay = document.createElement('div');
  overlay.id = 'hhcd-wecom-preview-modal';
  overlay.style.position = 'fixed';
  overlay.style.inset = '0';
  overlay.style.zIndex = '2147483647';
  overlay.style.background = 'rgba(15, 23, 42, 0.62)';
  overlay.style.display = 'flex';
  overlay.style.alignItems = 'center';
  overlay.style.justifyContent = 'center';
  overlay.style.padding = '24px';
  overlay.style.boxSizing = 'border-box';

  const frameWrap = document.createElement('div');
  frameWrap.style.position = 'relative';
  frameWrap.style.width = `${modalWidth}px`;
  frameWrap.style.height = `${modalHeight}px`;
  frameWrap.style.background = '#fff';
  frameWrap.style.boxShadow = '0 20px 60px rgba(0,0,0,0.24)';

  const close = document.createElement('button');
  close.type = 'button';
  close.textContent = 'x';
  close.style.position = 'absolute';
  close.style.top = '8px';
  close.style.right = '8px';
  close.style.zIndex = '1';
  close.style.width = '28px';
  close.style.height = '28px';
  close.style.border = '0';
  close.style.borderRadius = '14px';
  close.style.background = 'rgba(15, 23, 42, 0.72)';
  close.style.color = '#fff';
  close.style.cursor = 'pointer';
  close.onclick = () => overlay.remove();

  const iframe = document.createElement('iframe');
  iframe.src = modalUrl;
  iframe.style.width = '100%';
  iframe.style.height = '100%';
  iframe.style.border = '0';

  overlay.onclick = (event) => {
    if (event.target === overlay) overlay.remove();
  };
  frameWrap.appendChild(close);
  frameWrap.appendChild(iframe);
  overlay.appendChild(frameWrap);
  document.body.appendChild(overlay);
  return true;
}

/**
 * 初始化企业微信 JS-SDK 和 agentConfig 鉴权
 * 自动根据 UserAgent 区分企微内置浏览器或第三方浏览器，动态进行签名加载，
 * 鉴权成功后自动启动 MutationObserver 监控 DOM，实时绑定新插入的 open-data 组件
 */
export async function initWeComJSSDK(
  extraJsApiList: string[] = [],
  retryUrl?: string,
): Promise<boolean> {
  // 页面挂载时的空参数调用只是在预热 open-data；新版 OpenDataFrame 已启动时
  // 直接复用它，避免旧 wx.config/agentConfig 与 ww.register 并发鉴权。
  // 仍依赖旧版 SDK 的接口会携带明确列表按需初始化。
  if (extraJsApiList.length === 0 && !retryUrl && openDataFrameReadyPromise) {
    return openDataFrameReadyPromise;
  }

  const wxExists = typeof (window as any).wx !== 'undefined';
  const wwOpenDataExists = typeof (window as any).WWOpenData !== 'undefined';
  sendTelemetry(`initWeComJSSDK started. wwOpenDataExists=${wwOpenDataExists}, wxExists=${wxExists}`);

  // 动态载入 WeCom JSSDK 脚本（如尚未加载）
  sendTelemetry('Calling loadWeComScripts...');
  const scriptsLoaded = await loadWeComScripts();
  sendTelemetry(`loadWeComScripts returned: ${scriptsLoaded}. Current window.wx=${!!(window as any).wx}, window.WWOpenData=${!!(window as any).WWOpenData}`);
  if (!scriptsLoaded) {
    return false;
  }

  const wx = (window as any).wx;
  sendTelemetry(`initWeComJSSDK called. retryUrl=${retryUrl || 'none'} hasWx=${!!wx}`);
  if (!wx) {
    sendTelemetry('Error: wx is not defined on window');
    return false;
  }

  // 缓存额外的 API 列表以备单页路由更新签名时使用
  if (extraJsApiList.length > 0) {
    cachedExtraJsApiList = Array.from(new Set([...cachedExtraJsApiList, ...extraJsApiList]));
  }

  // 解决 iOS 和 Mac WeCom 客户端 (WKWebView 内核) 的单页应用 History 签名 URL 缓存 Bug。
  // 在 iOS/Mac 下，WeCom 的签名校验 URL 必须使用“进入应用时的初始页面 Entry URL”。
  const isWeComClientWebview = /wxwork|micromessenger|macwechat/i.test(navigator.userAgent);
  if (!isWeComClientWebview && (window as any).__wecom_ready) {
    sendTelemetry('initWeComJSSDK: Already authorized in external browser. Skipping re-initialization.');
    return true;
  }
  const isIOSorMac = /iphone|ipad|ipod|macintosh|mac os x/i.test(navigator.userAgent);
  const entryUrl = (window as any).__wecom_entry_url || window.location.href.split('#')[0];
  
  // 如果是重试，则使用传入的备用 URL；否则对于企微客户端内置浏览器且为 iOS/Mac，首选 Entry URL；对于普通浏览器，使用当前页 URL
  const currentUrl = retryUrl || ((isWeComClientWebview && isIOSorMac) ? entryUrl : window.location.href.split('#')[0]);
  lastInitializedUrl = window.location.href.split('#')[0];

  sendTelemetry(`URL selected for signature: ${currentUrl} (entryUrl: ${entryUrl}, location.href: ${window.location.href})`);

  // 动态启动单页路由监听（自动拦截 popstate, pushState, replaceState）
  initRouteListener();

  // 如果没有登录凭证（如在登录页面），无需初始化企微 JSSDK，直接返回以避免 401 未认证请求报错
  // 聊天工具栏 H5 可能先拿到企微回跳/持久化恢复的全局 token，
  // 此时 Zustand 尚未完成用户资料恢复；鉴权判断需与 apiClient 保持一致。
  const token = useAuthStore.getState().token || (window as any).__wecom_token__;
  if (!token) {
    sendTelemetry('Skipped: no token found in authStore');
    return false;
  }

  try {
    sendTelemetry(`Fetching signature configuration from backend...`);
    const res = await apiClient.get('/auth/wecom/js-config', {
      params: { url: currentUrl },
      suppressErrorToast: true,
    } as any);

    const configData = res.data?.data || res.data;
    if (!configData) {
      sendTelemetry('Error: failed to fetch JSSDK config from backend');
      return false;
    }

    const { corp_id, agent_id, nonce_str, timestamp, signature, agent_signature } = configData;
    sendTelemetry(`Backend signature received: corp_id=${corp_id}, agent_id=${agent_id}, timestamp=${timestamp}`);
    
    const jsApiList = Array.from(new Set(['thirdPartyOpenData', ...cachedExtraJsApiList]));

    // 链式执行 wx.config -> wx.ready -> wx.agentConfig 应用级注册，这是解密渲染 open-data 的关键权限来源
    const doConfigAndAgentConfig = () => new Promise<boolean>((resolve) => {
      // 提取共享的自愈重试逻辑
      const handleRetry = () => {
        if (!retryUrl) {
          const alternativeUrl = (currentUrl === entryUrl) ? window.location.href.split('#')[0] : entryUrl;
          if (alternativeUrl !== currentUrl) {
            sendTelemetry(`Retry triggered with alternative URL: ${alternativeUrl}`);
            resolve(initWeComJSSDK(extraJsApiList, alternativeUrl));
            return true;
          }
        }
        return false;
      };

      const callAgentConfig = () => {
        sendTelemetry(`Calling wx.agentConfig with agentid=${agent_id}...`);
        wx.agentConfig({
          corpid: corp_id,
          agentid: String(agent_id),
          timestamp,
          nonceStr: nonce_str,
          signature: agent_signature,
          jsApiList,
          success: (res: any) => {
            sendTelemetry(`wx.agentConfig SUCCESS: ${JSON.stringify(res)}`);
            (window as any).__wecom_ready = true;
            window.dispatchEvent(new CustomEvent('wecom-ready'));
            triggerWWOpenDataBind();
            startWWOpenDataObserver();
            resolve(true);
          },
          fail: (err: any) => {
            sendTelemetry(`wx.agentConfig FAILED: ${JSON.stringify(err)}`);
            if (!handleRetry()) {
              resolve(false);
            }
          },
        });
      };

      // 仅在企微客户端（内置浏览器）中需要先进行 wx.config 激活授权；
      // 普通外部浏览器（如 Chrome）可以直接且必须跳过 wx.config，直接调用 wx.agentConfig。
      if (!isWeComClientWebview) {
        sendTelemetry('Running in external browser (e.g. Chrome). Calling wx.agentConfig directly...');
        callAgentConfig();
      } else {
        sendTelemetry(`Calling wx.config... appId=${corp_id}`);
        wx.config({
          beta: true, // 必须声明 beta 为 true 才能在后续使用 wx.agentConfig
          debug: false,
          appId: corp_id,
          timestamp: timestamp,
          nonceStr: nonce_str,
          signature: signature,
          // 基础 config 与 agentConfig 必须注册相同的业务 API；否则部分
          // Android 企微版本会在调用 openEnterpriseChat 时返回 function not exist。
          jsApiList,
        });

        wx.ready(() => {
          sendTelemetry('wx.config ready triggered. Calling wx.agentConfig...');
          callAgentConfig();
        });

        wx.error((err: any) => {
          sendTelemetry(`wx.config ERROR: ${JSON.stringify(err)}`);
          if (!handleRetry()) {
            resolve(false);
          }
        });
      }
    });

    return await withTimeout(
      doConfigAndAgentConfig(),
      WECOM_SDK_TIMEOUT_MS,
      '企业微信 JS-SDK 初始化超时',
    );

  } catch (err) {
    sendTelemetry(`initWeComJSSDK exception: ${String(err)}`);
    return false;
  }
}

export async function openWeComEnterpriseChat(options: {
  userIds?: string;
  externalUserIds?: string;
  chatId?: string;
  groupName?: string;
}) {
  // 仅发送当前场景需要的字段。企微会把“客户 + 员工 + 非空群名”识别为
  // 创建群聊，而不是打开客户单聊；部分 Android 客户端也会拒绝多余空字段。
  const payload: Record<string, string> = options.chatId
    ? { chatId: options.chatId }
    : options.externalUserIds
      ? { externalUserIds: options.externalUserIds, groupName: '' }
      : options.userIds
        ? { userIds: options.userIds, groupName: '' }
        : {};

  if (!payload.chatId && !payload.externalUserIds && !payload.userIds) {
    throw new Error('缺少可打开的企微会话标识');
  }

  // 会话列表/详情已经通过新版 @wecom/jssdk 完成应用身份注册，优先复用
  // 这条鉴权链；它对 Android 新版企微的兼容性比旧 wx.qy 包装更稳定。
  const modernReady = await initWeComOpenDataFrame();
  if (modernReady && typeof ww.openEnterpriseChat === 'function') {
    try {
      const result = await ww.openEnterpriseChat(payload);
      sendTelemetry(`ww.openEnterpriseChat SUCCESS: ${JSON.stringify(result || {})}`);
      return result;
    } catch (error) {
      const errorText = JSON.stringify(error || {});
      sendTelemetry(`ww.openEnterpriseChat FAILED: ${errorText} payload=${JSON.stringify(payload)}`);
      // 只有客户端不识别新版接口时才回退旧 SDK；业务参数/权限错误直接上抛，
      // 避免同一次点击连续弹出两次企微窗口。
      if (!/function not exist|not support|unsupported/i.test(errorText)) {
        throw error;
      }
    }
  }

  const ready = await initWeComJSSDK(['openEnterpriseChat']);
  if (!ready) {
    throw new Error('企业微信 JS-SDK 鉴权失败');
  }

  const wx = (window as any).wx;

  return new Promise((resolve, reject) => {
    const success = (res: unknown) => {
      sendTelemetry(`openEnterpriseChat SUCCESS: ${JSON.stringify(res)}`);
      resolve(res);
    };
    const fail = (err: unknown) => {
      sendTelemetry(`openEnterpriseChat FAILED: ${JSON.stringify(err)} payload=${JSON.stringify(payload)}`);
      reject(err);
    };

    if (wx?.qy?.openEnterpriseChat) {
      wx.qy.openEnterpriseChat({ ...payload, success, fail });
      return;
    }

    if (wx?.openEnterpriseChat) {
      wx.openEnterpriseChat({ ...payload, success, fail });
      return;
    }

    if (wx?.invoke) {
      wx.invoke('openEnterpriseChat', payload, (res: any) => {
        const errMsg = String(res?.err_msg || res?.errmsg || '');
        if (!errMsg || errMsg.includes(':ok')) {
          success(res);
        } else {
          fail(res);
        }
      });
      return;
    }

    fail(new Error('当前环境不支持打开企微聊天'));
  });
}

export async function openWeComAppManagePage(options: {
  page?: 'permission' | 'datazone_chat_permission' | 'datazone_doc_permission' | 'datazone_knowledge_permission' | 'customer_acquisition_permission';
  suiteId?: string;
}) {
  const scriptsLoaded = await loadWeComScripts();
  if (!scriptsLoaded) {
    throw new Error('企业微信 JS-SDK 加载失败');
  }

  const currentUrl = window.location.href.split('#')[0];
  const config = await fetchWeComSignatureConfig(currentUrl);

  ww.register({
    corpId: config.corp_id,
    agentId: config.agent_id,
    jsApiList: ['openAppManage'],
    async getConfigSignature(url: string) {
      const nextConfig = await fetchWeComSignatureConfig(url || currentUrl);
      return {
        timestamp: nextConfig.timestamp,
        nonceStr: nextConfig.nonce_str,
        signature: nextConfig.signature,
      };
    },
    async getAgentConfigSignature(url: string) {
      const nextConfig = await fetchWeComSignatureConfig(url || currentUrl);
      return {
        timestamp: nextConfig.timestamp,
        nonceStr: nextConfig.nonce_str,
        signature: nextConfig.agent_signature,
      };
    },
  });

  const payload = {
    ...(options.page ? { page: options.page } : {}),
    ...(options.suiteId ? { suiteId: options.suiteId } : {}),
  };

  const openAppManage = (ww as any).openAppManage;
  if (typeof openAppManage === 'function') {
    try {
      const res = await openAppManage(payload);
      const errMsg = getWeComSDKErrorMessage(res);
      if (errMsg && errMsg !== 'openAppManage:ok' && !errMsg.includes(':ok')) {
        throw new Error(errMsg);
      }
      return res;
    } catch (error) {
      throw new Error(getWeComSDKErrorMessage(error, 'openAppManage:fail'));
    }
  }

  const wx = (window as any).wx;
  if (wx?.invoke) {
    return new Promise((resolve, reject) => {
      wx.invoke('openAppManage', payload, (res: any) => {
        const errMsg = getWeComSDKErrorMessage(res);
        if (!errMsg || errMsg === 'openAppManage:ok' || errMsg.includes(':ok')) {
          resolve(res);
          return;
        }
        reject(new Error(errMsg || 'openAppManage:fail'));
      });
    });
  }

  throw new Error('当前环境不支持打开应用管理页面');
}

export async function getCurrentExternalContactId(): Promise<string | null> {
  const ready = await initWeComOpenDataFrame();
  if (!ready) {
    return null;
  }
  const result = await invokeCurrentChatTargetApi('getCurExternalContact');
  return result?.userId ? String(result.userId) : null;
}

export type WeComChatToolbarTarget =
  | { type: 'single'; externalUserId: string }
  | { type: 'group'; chatId: string };

type ChatToolbarContextApi = 'getContext' | 'getCurExternalContact' | 'getCurExternalChat';
const CHAT_TOOLBAR_JS_API_LIST: ChatToolbarContextApi[] = [
  'getContext',
  'getCurExternalContact',
  'getCurExternalChat',
];

const invokeCurrentChatTargetApi = async (apiName: ChatToolbarContextApi) => {
  // 新版文档明确支持 ww.getContext / ww.getCurExternalContact /
  // ww.getCurExternalChat，且三个接口都要求通过应用身份注册。
  const modernApi = (ww as any)[apiName];
  if (typeof modernApi === 'function') {
    try {
      return await modernApi();
    } catch (error) {
      sendTelemetry(`${apiName} failed: ${String(error)}`);
      const message = getWeComSDKErrorMessage(error);
      if (!/function not exist|not support|unsupported/i.test(message)) {
        return null;
      }
    }
  }

  // 仅兼容尚未提供新版方法的旧企业微信客户端；正常路径不会再启动
  // wx.config/agentConfig，也不会与 OpenDataFrame 的 ww.register 竞争。
  const legacyReady = await initWeComJSSDK(CHAT_TOOLBAR_JS_API_LIST);
  if (!legacyReady) return null;
  const wx = (window as any).wx;
  if (wx?.invoke) {
    return new Promise<any>((resolve) => {
      wx.invoke(apiName, {}, (res: any) => resolve(res));
    });
  }

  return null;
};

/**
 * 识别聊天工具栏当前上下文：客户单聊返回 externalUserId，客户群聊返回 chatId。
 * 三个接口都通过 ww.register 以应用身份注册，并从对应聊天工具栏入口调用。
 */
export async function getCurrentChatToolbarTarget(): Promise<WeComChatToolbarTarget | null> {
  const ready = await initWeComOpenDataFrame();
  if (!ready) {
    return null;
  }

  // 企业微信文档要求先通过 getContext 判断聊天工具栏入口，再调用唯一匹配的上下文接口。
  // 不能通过依次试调用两个接口来猜测单聊/群聊，否则失败回调和旧上下文可能造成误判。
  const contextRes = await invokeCurrentChatTargetApi('getContext');
  const entry = String(contextRes?.entry || '');

  if (entry === 'single_chat_tools') {
    const contactRes = await invokeCurrentChatTargetApi('getCurExternalContact');
    const externalUserId = contactRes?.userId;
    if (externalUserId) {
      return { type: 'single', externalUserId: String(externalUserId) };
    }
    sendTelemetry(`Unable to resolve single chat target: entry=${entry}, contact=${JSON.stringify(contactRes || {})}`);
    return null;
  }

  if (entry === 'group_chat_tools') {
    const chatRes = await invokeCurrentChatTargetApi('getCurExternalChat');
    const chatId = chatRes?.chatId;
    if (chatId) {
      return { type: 'group', chatId: String(chatId) };
    }
    sendTelemetry(`Unable to resolve group chat target: entry=${entry}, chat=${JSON.stringify(chatRes || {})}`);
    return null;
  }

  sendTelemetry(`Unsupported chat toolbar context: ${JSON.stringify(contextRes || {})}`);
  return null;
}

/**
 * 触发页面上所有 ww-open-data 标签 of DOM 并绑定
 */
export function triggerWWOpenDataBind() {
  const WWOpenData = (window as any).WWOpenData;
  const elements = document.querySelectorAll('ww-open-data');
  sendTelemetry(`triggerWWOpenDataBind called. WWOpenData exists=${!!WWOpenData}, found elements count=${elements.length}`);
  if (elements.length > 0) {
    const sample = Array.from(elements)
      .slice(0, 6)
      .map((el) => ({
        type: el.getAttribute('type'),
        openid: el.getAttribute('openid'),
        id: el.getAttribute('id'),
      }));
    sendTelemetry(`ww-open-data sample: ${JSON.stringify(sample)}`);
  }
  if (WWOpenData) {
    try {
      WWOpenData.bindAll(elements);
      sendTelemetry('WWOpenData.bindAll executed successfully');
    } catch (err) {
      sendTelemetry(`WWOpenData.bindAll threw error: ${String(err)}`);
    }
  }
}

/**
 * 启动全局 MutationObserver 监控 DOM。
 * 解决单页应用中 React 动态渲染可能落后于 JS-SDK 鉴权成功时机的问题，
 * 任何新插入的 <ww-open-data> 元素都会在第一时间被自动捕获并绑定渲染！
 */
export function startWWOpenDataObserver() {
  if (observerStarted) return;
  const WWOpenData = (window as any).WWOpenData;
  if (!WWOpenData) {
    return;
  }

  const observer = new MutationObserver((mutations) => {
    const newElements: Element[] = [];
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          const el = node as Element;
          if (el.tagName.toLowerCase() === 'ww-open-data') {
            newElements.push(el);
          } else {
            const nested = el.querySelectorAll('ww-open-data');
            if (nested.length > 0) {
              nested.forEach((item) => newElements.push(item));
            }
          }
        }
      });
    });

    if (newElements.length > 0) {
      // 过滤并标记已绑定的元素，彻底防止 MutationObserver 递归无限绑定卡死浏览器！
      const unboundElements = newElements.filter((el) => {
        if (el.getAttribute('data-wecom-bound') === 'true') return false;
        el.setAttribute('data-wecom-bound', 'true');
        return true;
      });

      if (unboundElements.length > 0) {
        try {
          WWOpenData.bindAll(unboundElements);
        } catch {
          // Binding failures are non-fatal; existing fallback text remains visible.
        }
      }
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
  observerStarted = true;
}

/**
 * 启动全局单页路由监听。
 * 拦截浏览器 history API (pushState/replaceState) 与 popstate 事件，
 * 保证在 SPA 路由跳转后，自动为新 URL 重新进行 JS-SDK / agentConfig 签名鉴权。
 */
export function initRouteListener() {
  if (isRouteListenerInitialized) return;
  isRouteListenerInitialized = true;

  const handleUrlChange = () => {
    const currentUrl = window.location.href.split('#')[0];
    if (currentUrl === lastInitializedUrl) return;

    // OpenDataFrame 已由 ww.register 管理时，新版 SDK 会针对新 URL 自动调用
    // getConfigSignature/getAgentConfigSignature。此处再执行旧版
    // wx.config/agentConfig 会与其并发并覆盖权限状态，快速切换页面时会造成
    // wwapp.getOpenData:fail。需要使用旧版 JSAPI 的具体操作仍会主动调用
    // initWeComJSSDK，不依赖这个路由预热。
    if (openDataFrameReadyPromise) {
      lastInitializedUrl = currentUrl;
      return;
    }

    initWeComJSSDK(cachedExtraJsApiList);
  };

  // 1. 监听 popstate 浏览器导航 (前进/后退或 React Router 触发)
  window.addEventListener('popstate', handleUrlChange);

  // 2. 拦截 History.pushState
  const origPushState = window.history.pushState;
  if (origPushState && !(origPushState as any).__wrapped) {
    const wrappedPushState = function (this: any, ...args: Parameters<typeof origPushState>) {
      const result = origPushState.apply(window.history, args);
      setTimeout(handleUrlChange, 50); // 延时一小段确保 DOM & URL 已就绪
      return result;
    };
    (wrappedPushState as any).__wrapped = true;
    window.history.pushState = wrappedPushState;
  }

  // 3. 拦截 History.replaceState
  const origReplaceState = window.history.replaceState;
  if (origReplaceState && !(origReplaceState as any).__wrapped) {
    const wrappedReplaceState = function (this: any, ...args: Parameters<typeof origReplaceState>) {
      const result = origReplaceState.apply(window.history, args);
      setTimeout(handleUrlChange, 50);
      return result;
    };
    (wrappedReplaceState as any).__wrapped = true;
    window.history.replaceState = wrappedReplaceState;
  }
}
